import { useEffect, useRef } from 'react';

class Particle {
  constructor(canvas) {
    this.canvas = canvas;
    this.x = Math.random() * canvas.width;
    this.y = Math.random() * canvas.height;
    this.z = Math.random() * 100; // 3D depth
    this.size = Math.random() * 2.5 + 0.5;
    this.speedX = Math.random() * 0.3 - 0.15;
    this.speedY = Math.random() * 0.3 - 0.15;
    this.speedZ = Math.random() * 0.5 - 0.25;
    this.opacity = Math.random() * 0.7 + 0.3;
    this.twinkle = Math.random() * 0.05 + 0.01;
    this.hue = Math.random() * 60 + 190; // Blue to purple range
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;
    this.z += this.speedZ;
    this.opacity += this.twinkle;
    if (this.opacity > 1 || this.opacity < 0.2) {
      this.twinkle *= -1;
    }

    // Wrap around edges with 3D effect
    if (this.x > this.canvas.width) this.x = 0;
    if (this.x < 0) this.x = this.canvas.width;
    if (this.y > this.canvas.height) this.y = 0;
    if (this.y < 0) this.y = this.canvas.height;
    if (this.z > 100) this.z = 0;
    if (this.z < 0) this.z = 100;
  }

  draw(ctx) {
    const scale = 1 + this.z / 100;
    const adjustedSize = this.size * scale;
    const adjustedOpacity = this.opacity * (0.5 + this.z / 200);
    
    // Create gradient for 3D depth effect
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, adjustedSize * 2);
    gradient.addColorStop(0, `hsla(${this.hue}, 80%, 70%, ${adjustedOpacity})`);
    gradient.addColorStop(0.5, `hsla(${this.hue}, 70%, 60%, ${adjustedOpacity * 0.6})`);
    gradient.addColorStop(1, `hsla(${this.hue}, 60%, 50%, 0)`);
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, adjustedSize, 0, Math.PI * 2);
    ctx.fill();
  }
}

class FloatingOrb {
  constructor(canvas) {
    this.canvas = canvas;
    this.x = Math.random() * canvas.width;
    this.y = Math.random() * canvas.height;
    this.radius = Math.random() * 80 + 40;
    this.speedX = Math.random() * 0.5 - 0.25;
    this.speedY = Math.random() * 0.5 - 0.25;
    this.hue = Math.random() * 40 + 200; // Blue to purple
    this.opacity = Math.random() * 0.15 + 0.05;
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;

    if (this.x > this.canvas.width + this.radius) this.x = -this.radius;
    if (this.x < -this.radius) this.x = this.canvas.width + this.radius;
    if (this.y > this.canvas.height + this.radius) this.y = -this.radius;
    if (this.y < -this.radius) this.y = this.canvas.height + this.radius;
  }

  draw(ctx) {
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
    gradient.addColorStop(0, `hsla(${this.hue}, 80%, 60%, ${this.opacity})`);
    gradient.addColorStop(0.5, `hsla(${this.hue}, 70%, 50%, ${this.opacity * 0.5})`);
    gradient.addColorStop(1, `hsla(${this.hue}, 60%, 40%, 0)`);
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

export default function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Enhanced particle system
    const particles = [];
    const particleCount = 180;
    
    // Floating orbs for depth
    const orbs = [];
    const orbCount = 5;
    
    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle(canvas));
    }
    
    // Create orbs
    for (let i = 0; i < orbCount; i++) {
      orbs.push(new FloatingOrb(canvas));
    }

    let mouseX = canvas.width / 2;
    let mouseY = canvas.height / 2;
    let targetMouseX = mouseX;
    let targetMouseY = mouseY;

    // Mouse move tracking for interactive effect
    const handleMouseMove = (e) => {
      targetMouseX = e.clientX;
      targetMouseY = e.clientY;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Smooth mouse follow
      mouseX += (targetMouseX - mouseX) * 0.05;
      mouseY += (targetMouseY - mouseY) * 0.05;

      // Draw orbs first (background layer)
      orbs.forEach(orb => {
        orb.update();
        orb.draw(ctx);
      });

      // Draw connections between nearby particles
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw(ctx);

        // Interactive effect - particles react to mouse
        const dx = mouseX - particles[i].x;
        const dy = mouseY - particles[i].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          const force = (150 - distance) / 150;
          particles[i].x -= dx * force * 0.01;
          particles[i].y -= dy * force * 0.01;
        }

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const opacity = 0.18 * (1 - distance / 150);
            const gradient = ctx.createLinearGradient(
              particles[i].x, particles[i].y,
              particles[j].x, particles[j].y
            );
            gradient.addColorStop(0, `rgba(99, 102, 241, ${opacity})`);
            gradient.addColorStop(0.5, `rgba(139, 92, 246, ${opacity * 1.2})`);
            gradient.addColorStop(1, `rgba(59, 130, 246, ${opacity})`);
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      requestAnimationFrame(animate);
    }

    animate();

    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-1"
      style={{ opacity: 0.4 }}
    />
  );
}
